/**
 * Floating score labels at catch / merge positions: chunky inked cartoon lettering (Fredoka
 * 700, an ink outline with a hard ink lip under it, a lit fill that is white on top) that pops
 * in with an overshoot, rises and fades. The word is baked once (render/juice/juice-text.js)
 * and only scaled per frame: one sprite in Pixi, one drawImage in Canvas 2D.
 *
 * Chain merges spawn several popups at nearly the same spot within a few frames. Each popup
 * finds its place once, on its first draw (`placeScorePopup`): if it would land on a popup that
 * is still young (popping in or just risen), it moves up a line and a little to the side, so a
 * chain reads as a stack of numbers instead of one knot.
 */

import * as C from "../config/constants.js";
import { fallCountdownFontSizePx } from "../layout/mockup-ui-scale.js";
import { getInkTextSprite } from "../render/juice/juice-text.js";
import { INK } from "../render/toon/palette.js";

var DEFAULT_DURATION_MS = 850;
var DEFAULT_RISE_PX = 34;
var SCORE_POPUP_FONT_FAMILY = "Fredoka, 'Baloo 2', sans-serif";
var SCORE_POPUP_FONT_WEIGHT = 700;
/** Lit fill of the lettering: gold reads on the day sky and the night sea alike. */
export var SCORE_POPUP_COLOR = "#ffd45c";
/** Big rewards (six digits and up: the Dragon bonus) get a hotter colour. */
var SCORE_POPUP_BIG_COLOR = "#ff8fd0";
var SCORE_POPUP_BIG_DIGITS = 6;
var SCORE_POPUP_FILL_SIZE_RATIO = 0.97;
var SCORE_POPUP_STROKE_WIDTH_RATIO = 0.16;
/** Sub-pixel floor so tiny popups never disappear; stays proportional above this. */
var SCORE_POPUP_STROKE_WIDTH_MIN = 0.75;
/** A popup keeps its spot against newcomers for this share of its life. */
var STACK_YOUNG_SHARE = 0.4;
/** Clearance between stacked popups, in line heights; most moves one popup makes. */
var STACK_CLEAR_LINES = 1.2;
var STACK_MAX_STEPS = 5;
/** Rough glyph advance of Fredoka 700 digits (em), to size a popup before it is baked. */
var STACK_GLYPH_EM = 0.62;

/**
 * @param {number} peakFontPx
 */
export function computeScorePopupStrokeWidth(peakFontPx) {
  var peak = Number(peakFontPx);
  if (!isFinite(peak) || peak <= 0) return SCORE_POPUP_STROKE_WIDTH_MIN;
  return Math.max(SCORE_POPUP_STROKE_WIDTH_MIN, peak * SCORE_POPUP_STROKE_WIDTH_RATIO);
}

/**
 * Peak popup font size — ~50% of Fall countdown peak (countdown uses flaskH × 0.6).
 *
 * @param {{ runwayFlaskDisplayHeight?: number }|null} [scale]
 * @param {{ scaleSide?: number }|null} [mockupScale]
 * @param {object} [cfg]
 */
export function computeScorePopupPeakFontPx(scale, mockupScale, cfg) {
  cfg = cfg || (C.RUN_CONFIG || {});
  var countdownRatio = cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO != null
    ? Number(cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO)
    : 0.6;
  var popupRatio = cfg.SCORE_POPUP_COUNTDOWN_HEIGHT_RATIO != null
    ? Number(cfg.SCORE_POPUP_COUNTDOWN_HEIGHT_RATIO)
    : 0.5;
  var flaskH = scale && scale.runwayFlaskDisplayHeight != null
    ? Number(scale.runwayFlaskDisplayHeight)
    : NaN;
  if (isFinite(flaskH) && flaskH > 0) {
    return Math.max(1, Math.round(flaskH * countdownRatio * popupRatio));
  }
  var basePx = cfg.FALL_COUNTDOWN_FONT_BASE_PX != null ? cfg.FALL_COUNTDOWN_FONT_BASE_PX : 72;
  var side = mockupScale && mockupScale.scaleSide != null ? Number(mockupScale.scaleSide) : 1;
  var countdownPeak = fallCountdownFontSizePx(side, basePx);
  return Math.max(1, Math.round(countdownPeak * popupRatio));
}

/**
 * @param {number} x
 * @param {number} y
 * @param {string} text
 * @param {{ durationMs?: number, risePx?: number, color?: string }} [opts]
 */
export function createScorePopup(x, y, text, opts) {
  opts = opts || {};
  return {
    x: x,
    y: y,
    text: text,
    startMs: Date.now(),
    durationMs: opts.durationMs != null ? opts.durationMs : DEFAULT_DURATION_MS,
    risePx: opts.risePx != null ? opts.risePx : DEFAULT_RISE_PX,
    scale: 0.4,
    color: opts.color || null,
    // Stacking offset, settled on the first draw (placeScorePopup); null until then.
    stackDx: 0,
    stackDy: null,
  };
}

/** How far a popup has risen at life t (0–1): fast, then hanging. */
function riseAt(p, t) {
  return p.risePx * (1 - (1 - t) * (1 - t));
}

/**
 * Settle where popup `index` sits (its stackDx / stackDy), once: up one line (and a little to
 * alternating sides) for as long as it would overlap a popup that is already placed and still
 * in the first STACK_YOUNG_SHARE of its life. Older popups have risen and faded out of the way.
 * @param {ReturnType<typeof createScorePopup>[]} popups
 * @param {number} index
 * @param {number} now ms
 * @param {number} fontPx the popups' font size (px)
 */
export function placeScorePopup(popups, index, now, fontPx) {
  var p = popups[index];
  if (!p || p.stackDy != null) return;
  var lineH = fontPx * 1.05;
  var halfW = (String(p.text).length * fontPx * STACK_GLYPH_EM) / 2;
  var t0 = Math.max(0, Math.min(1, (now - p.startMs) / p.durationMs));
  var dx = 0, dy = 0;
  for (var step = 0; step < STACK_MAX_STEPS; step++) {
    var blocked = false;
    for (var j = 0; j < popups.length && !blocked; j++) {
      var q = popups[j];
      if (j === index || !q || q.stackDy == null) continue;
      var tq = (now - q.startMs) / q.durationMs;
      if (tq < 0 || tq > STACK_YOUNG_SHARE) continue;
      var qx = q.x + q.stackDx, qy = q.y + q.stackDy - riseAt(q, tq);
      var px = p.x + dx, py = p.y + dy - riseAt(p, t0);
      var qHalfW = (String(q.text).length * fontPx * STACK_GLYPH_EM) / 2;
      blocked = Math.abs(py - qy) < lineH * STACK_CLEAR_LINES && Math.abs(px - qx) < halfW + qHalfW;
    }
    if (!blocked) break;
    dy -= lineH;
    dx = (step % 2 ? -1 : 1) * lineH * 0.35;
  }
  p.stackDx = dx;
  p.stackDy = dy;
}

/**
 * @param {ReturnType<typeof createScorePopup>[]} popups
 * @param {number} [nowMs]
 */
export function pruneScorePopups(popups, nowMs) {
  if (!popups || !popups.length) return popups;
  var now = nowMs != null ? nowMs : Date.now();
  var out = [];
  for (var i = 0; i < popups.length; i++) {
    var p = popups[i];
    if (now - p.startMs < p.durationMs + 50) out.push(p);
  }
  return out;
}

/**
 * Pop curve over the popup's life t (0–1): from `popMin` up past 1 (overshoot 1.22), settle
 * to 1, hold, then shrink a little while it fades.
 * @param {number} t
 * @param {number} [popMin]
 */
export function scorePopupScale(t, popMin) {
  var lo = popMin != null ? popMin : 0.5;
  if (t < 0.1) {
    var a = t / 0.1;
    var e = 1 - (1 - a) * (1 - a) * (1 - a);
    return lo + (1.22 - lo) * e;
  }
  if (t < 0.24) {
    var b = (t - 0.1) / 0.14;
    return 1.22 - 0.22 * (b * b * (3 - 2 * b));
  }
  if (t > 0.7) return 1 - ((t - 0.7) / 0.3) * 0.25;
  return 1;
}

/** Fill colour for a popup's text (big six-digit rewards get the hot colour). */
export function scorePopupColor(text) {
  var digits = String(text || "").replace(/[^0-9]/g, "");
  return digits.length >= SCORE_POPUP_BIG_DIGITS ? SCORE_POPUP_BIG_COLOR : SCORE_POPUP_COLOR;
}

/**
 * Loader warm-up (core/play-warmup.js): bake a popup word exactly as drawScorePopups will
 * (same font size, colour and density), so the first catch or merge only places a sprite.
 * @param {string} text
 * @param {number} peakFontPx as passed to drawScorePopups (`opts.peakFontPx`)
 * @param {number} [dpr] playfield resolution (undefined on Canvas 2D, as the draw passes)
 * @returns {HTMLCanvasElement|null}
 */
export function bakeScorePopupText(text, peakFontPx, dpr) {
  var peak = Number(peakFontPx);
  if (!isFinite(peak) || peak <= 0) peak = 24;
  var fillFontPx = Math.max(1, Math.round(peak * SCORE_POPUP_FILL_SIZE_RATIO));
  var baked = getInkTextSprite(String(text), scorePopupColor(text), fillFontPx, { dpr: dpr });
  return baked ? baked.canvas : null;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ReturnType<typeof createScorePopup>[]} popups
 * @param {number} [nowMs]
 * @param {{ peakFontPx?: number, popScaleMin?: number, playfield?: object, dpr?: number }} [opts]
 */
export function drawScorePopups(ctx, popups, nowMs, opts) {
  if ((!ctx && !(opts && opts.playfield)) || !popups || !popups.length) return;
  opts = opts || {};
  var cfg = C.RUN_CONFIG || {};
  var peakFontPx = opts.peakFontPx != null
    ? Number(opts.peakFontPx)
    : computeScorePopupPeakFontPx(null, null, cfg);
  if (!isFinite(peakFontPx) || peakFontPx <= 0) peakFontPx = 24;
  var popMin = opts.popScaleMin != null
    ? Number(opts.popScaleMin)
    : (cfg.FALL_COUNTDOWN_POP_SCALE_MIN != null ? Number(cfg.FALL_COUNTDOWN_POP_SCALE_MIN) : 0.5);
  if (!isFinite(popMin) || popMin <= 0) popMin = 0.5;
  popMin = Math.min(1, popMin);

  var playfield = opts.playfield;
  var dpr = opts.dpr > 0 ? opts.dpr : ((playfield && playfield.resolution) || undefined);
  var now = nowMs != null ? nowMs : Date.now();
  var fillFontPx = Math.max(1, Math.round(peakFontPx * SCORE_POPUP_FILL_SIZE_RATIO));
  for (var i = 0; i < popups.length; i++) {
    var p = popups[i];
    var elapsed = now - p.startMs;
    if (elapsed < 0 || elapsed >= p.durationMs) continue;
    placeScorePopup(popups, i, now, fillFontPx);
    var t = elapsed / p.durationMs;
    var scale = scorePopupScale(t, popMin);
    var alpha = t < 0.06 ? t / 0.06 : (t > 0.62 ? 1 - (t - 0.62) / 0.38 : 1);
    alpha = Math.max(0, Math.min(1, alpha));
    // Rise fast, then hang (ease-out over the life), from the popup's settled spot.
    var px = p.x + (p.stackDx || 0);
    var y = p.y + (p.stackDy || 0) - riseAt(p, t);
    var color = p.color || scorePopupColor(p.text);
    var baked = getInkTextSprite(p.text, color, fillFontPx, { dpr: dpr });
    if (playfield && typeof playfield.overlay === "function") {
      if (baked) {
        playfield.overlay({
          key: "score-" + i,
          kind: "texture",
          image: baked.canvas,
          x: px,
          y: y,
          width: baked.w * scale,
          height: baked.h * scale,
          alpha: alpha,
          zIndex: 60,
        });
        continue;
      }
      playfield.overlay({
        key: "score-" + i + "-" + p.text,
        kind: "label",
        x: px,
        y: y,
        text: p.text,
        fontSize: fillFontPx,
        fontFamily: SCORE_POPUP_FONT_FAMILY,
        fontWeight: SCORE_POPUP_FONT_WEIGHT,
        fill: 0xffd45c,
        strokeFill: 0x172a3a,
        stroke: computeScorePopupStrokeWidth(peakFontPx),
        scale: scale,
        alpha: alpha,
        zIndex: 60,
      });
      continue;
    }
    if (!ctx) continue;
    if (baked && typeof ctx.drawImage === "function") {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.drawImage(baked.canvas, px - (baked.w * scale) / 2, y - (baked.h * scale) / 2, baked.w * scale, baked.h * scale);
      ctx.restore();
      continue;
    }
    // Nothing to bake into (no 2D canvas): the same lettering drawn directly.
    ctx.save();
    ctx.translate(px, y);
    ctx.scale(scale, scale);
    ctx.font = SCORE_POPUP_FONT_WEIGHT + " " + fillFontPx + "px " + SCORE_POPUP_FONT_FAMILY;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.globalAlpha = alpha;
    ctx.lineWidth = computeScorePopupStrokeWidth(peakFontPx);
    ctx.strokeStyle = INK;
    ctx.fillStyle = color;
    ctx.strokeText(p.text, 0, 0);
    ctx.fillText(p.text, 0, 0);
    ctx.restore();
  }
}
